import { FEATURE_FLAG_DEFINITIONS } from './featureFlags';
import {
  AnalyticsAdapter,
  EventProperties,
  InitializeOptions,
  UserProperties
} from './types';

// The SDK (~275KB min) is loaded via dynamic import in initialize() so it ships
// as an async chunk instead of blocking the critical-path bundle. This type
// alias keeps full typing without a runtime import. (The package uses
// `export =`, so the module type IS the sdk instance.)
type CleverTapSdk = typeof import('clevertap-web-sdk');

type Region = 'sg1' | 'in1' | 'us1' | 'aps3' | 'mec1' | 'eu1' | 'sk1';

// Minimal shape of the Variable handle returned by clevertap.defineVariable.
// getValue() returns the default until fetchVariables resolves, then the server
// value. Unlike the top-level clevertap.getVariableValue(), it does NOT throw
// when the VARIABLES storage is empty (e.g. first load before any fetch).
interface CleverTapVariable {
  getValue: () => string | number | boolean | null;
}

export class CleverTapAdapter implements AnalyticsAdapter {
  private initialized: boolean = false;

  // Resolves once the dynamically imported SDK is loaded and configured.
  // Null until initialize() is called.
  private ready: Promise<CleverTapSdk> | null = null;

  // Run fn once the SDK is ready; calls made before initialize() are dropped
  // with a warn (same behavior as the old `initialized` guard). Calls made
  // after initialize() but before the chunk resolves are queued on the promise
  // chain, so no events are lost to the async load.
  private withSdk(fn: (ct: CleverTapSdk) => void): void {
    if (!this.ready) {
      console.warn('CleverTap not initialized');
      return;
    }
    this.ready
      .then(fn)
      .catch((e) => console.warn('CleverTap: SDK call failed', e));
  }

  // Handles for defined feature-flag variables, keyed by name. Read via
  // getValue() in fetchVariant() instead of the crash-prone top-level getter.
  private variables: Record<string, CleverTapVariable> = {};

  // Everyone waiting to hear that flag values changed — one entry per mounted
  // useFlag() call. The SDK tells us when new values land (see
  // addVariablesChangedCallback in initialize) and we pass that on to each
  // listener here, which is what makes the screen re-render. Without it, a
  // value that arrives after the first paint would never be shown.
  private flagListeners = new Set<() => void>();

  // Flags are fetched twice: anonymously at boot, and again once login
  // identifies the user (userReducers). Values can differ between the two when
  // an experiment targets a segment, so anything that must not change after it
  // is first shown — a whole-page variant — waits for the identified values.
  // `identified` flips when setUserId runs; the next values that land after
  // that are the identified user's. A returning session (reload, new tab)
  // never logs in again, but the SDK still holds that user's identity from
  // last time, so initialize() takes `identified` from the caller instead.
  // Don't re-run onUserLogin on boot to get there: called on every load it
  // made CleverTap alternate the served value between loads.
  // ponytail: an anonymous response landing after setUserId (a slow boot fetch)
  // also counts as ready; correlate responses to requests if that shows up.
  private identified = false;

  private flagsReady = false;

  // Deduplication: track last event to prevent duplicates from touch + click firing together
  private lastEvent: {
    name: string;
    properties: string;
    timestamp: number;
  } | null = null;

  private static DEDUP_THRESHOLD_MS = 100;

  private isDuplicateEvent(
    eventName: string,
    eventProperties: EventProperties
  ): boolean {
    const now = Date.now();
    const propsString = JSON.stringify(eventProperties);

    if (
      this.lastEvent &&
      this.lastEvent.name === eventName &&
      this.lastEvent.properties === propsString &&
      now - this.lastEvent.timestamp < CleverTapAdapter.DEDUP_THRESHOLD_MS
    ) {
      console.log('Duplicate event suppressed:', eventName);
      return true;
    }

    this.lastEvent = {
      name: eventName,
      properties: propsString,
      timestamp: now
    };
    return false;
  }

  initialize(apiKey: string, options: InitializeOptions): void {
    if (!this.initialized) {
      // Mark initialized synchronously so the facade accepts calls; actual SDK
      // work is queued on `ready` and flushes once the async chunk lands.
      this.initialized = true;
      this.identified = options.identified === true;

      this.ready = import('clevertap-web-sdk').then((m) => {
        // Webpack's CJS interop puts the `export =` value on `.default`.
        const ct = (m as { default: CleverTapSdk }).default ?? m;
        // Initialize CleverTap with account ID
        const region =
          typeof options.region === 'string' ? options.region : undefined;
        const targetDomain =
          typeof options.targetDomain === 'string'
            ? options.targetDomain
            : undefined;

        ct.init(apiKey, region as Region, targetDomain);
        ct.spa = true; // Required for SPA

        // Set privacy settings if needed
        if (typeof options.useIP === 'boolean') {
          ct.privacy.push({ useIP: options.useIP });
        }

        // Enable debugging in development
        // if (import.meta.env.DEV) {
        //   ct.setLogLevel(3); // 0: off, 1: error, 2: info, 3: debug
        // }

        console.log('CleverTap analytics initialized');

        // The SDK's own change notification. Prefer it over piggybacking on the
        // fetchVariables callback for two reasons: that callback is a single
        // slot the SDK overwrites (our second fetchExperiment() would drop the
        // first's), and variables also merge from any server response carrying
        // `vars`, which no fetch callback of ours would ever see. One SDK
        // registration fans out to every subscriber.
        ct.addVariablesChangedCallback(() => {
          if (this.identified) this.flagsReady = true;
          this.flagListeners.forEach((listener) => listener());
        });

        // Register feature-flag variables with their local defaults so reads
        // resolve to a sensible value before the first fetchVariables() lands.
        // See ./featureFlags.
        FEATURE_FLAG_DEFINITIONS.forEach(({ name, defaultValue }) => {
          try {
            const variable = ct.defineVariable(
              name,
              defaultValue
            ) as unknown as CleverTapVariable | null;
            if (variable) {
              this.variables[name] = variable;
            }
          } catch (e) {
            console.warn(`CleverTap: failed to define variable "${name}"`, e);
          }
        });

        return ct;
      });
    }
  }

  setUserId(userId: string | null): void {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return;
    }
    if (!userId) {
      console.warn('User ID not provided');
      return;
    }

    console.log('Clevertap user id set', userId);
    this.identified = true;
    this.withSdk((ct) => {
      ct.onUserLogin.push({
        Site: {
          Identity: userId
        }
      });
    });
  }

  trackEvent(eventName: string, eventProperties: EventProperties = {}): void {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    // Skip duplicate events (e.g., from onClick + onTouchStart firing together)
    if (this.isDuplicateEvent(eventName, eventProperties)) {
      return;
    }

    this.withSdk((ct) => {
      ct.event.push(eventName, eventProperties);
      console.log('Event pushed:', { eventName, eventProperties });
    });
  }

  setUserProperties(properties: UserProperties): void {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    // Filter out null values and prepare profile object

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const profileData: Record<string, any> = {
      Site: {}
    };

    Object.entries(properties).forEach(([key, value]) => {
      if (value !== null) {
        profileData.Site[key] = value;
      }
    });

    console.log('Profile data clevertap::', profileData);

    // Push profile updates to CleverTap
    this.withSdk((ct) => {
      ct.profile.push(profileData);
    });
  }

  setGroup(groupType: string, groupName: string | string[]): void {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    // CleverTap doesn't have native group functionality like Amplitude
    // We can implement it using custom user properties; ensure values are primitive types
    const groupProperty = `group_${groupType}`;

    // Convert arrays to a string so the value is a permitted SiteData type
    this.withSdk((ct) => {
      ct.profile.push({
        Site: {
          [groupProperty]: Array.isArray(groupName)
            ? groupName.join(',')
            : groupName
        }
      });
    });

    console.log(
      `Group set: ${groupType} = ${
        Array.isArray(groupName) ? groupName.join(',') : groupName
      }`
    );
  }

  setUserPathInLocalStorage(userPath: string): void {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return;
    }
    localStorage.setItem('user_path', userPath);
  }

  getUserPathFromLocalStorage(): string {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return '';
    }
    return localStorage.getItem('user_path') || '';
  }

  // CleverTap Remote Config (feature flags / A/B testing)

  // Ask CleverTap for the latest variable values for the current profile.
  //
  // Fire-and-forget by design: nothing waits on this. The UI updates through
  // addVariablesChangedCallback (see initialize) whenever values actually land,
  // which also covers merges this call didn't cause. Until then, reads fall
  // back to the local defaults, so a slow or dead network costs nothing.
  fetchExperiment(): void {
    this.withSdk((ct) => ct.fetchVariables());
  }

  // Register a function to be called whenever flag values change, and get back
  // a function that unregisters it. useFlag() calls this when a component
  // mounts and calls the returned one when it unmounts, so listeners from dead
  // components don't pile up.
  //
  // Safe to call before the CleverTap chunk has finished downloading: this only
  // touches our own Set. Whenever the SDK does finish loading, it registers a
  // single callback that reads this Set at the moment values arrive, so
  // anything added in the meantime is included.
  subscribeToFlags(listener: () => void): () => void {
    this.flagListeners.add(listener);
    return () => {
      this.flagListeners.delete(listener);
    };
  }

  isFlagsReady(): boolean {
    return this.flagsReady;
  }

  // On logout: whoever logs in next in this tab must wait for their own
  // values, not inherit "ready" from the previous user.
  resetFlags(): void {
    this.identified = false;
    this.flagsReady = false;
    this.flagListeners.forEach((listener) => listener());
  }

  // Read a resolved feature-flag value, normalized to the shared variant shape.
  // Returns null when the flag is unknown/unresolved so callers fall back to
  // their own default.
  fetchVariant(
    flagKey: string
  ): { value: string | null; payload?: Record<string, unknown> } | null {
    if (!this.initialized) {
      console.warn('CleverTap not initialized');
      return null;
    }
    const variable = this.variables[flagKey];
    if (!variable) {
      return null;
    }
    const value = variable.getValue();
    if (value === undefined || value === null) {
      return null;
    }
    return { value: String(value) };
  }
}
